import { z } from 'zod';

const boolParam = z.enum(['true', 'false']).transform(v => v === 'true');

export const MAX_CLIENT_LIST_TAG_FILTERS = 20;
export const MAX_CLIENT_LIST_TAG_LENGTH = 120;

const tagListParam = z
  .union([z.string(), z.array(z.string())])
  .optional()
  .transform(v => {
    const list = (Array.isArray(v) ? v : v !== undefined ? [v] : [])
      .map(t => t.trim())
      .filter(t => t.length > 0 && t.length <= MAX_CLIENT_LIST_TAG_LENGTH)
      .slice(0, MAX_CLIENT_LIST_TAG_FILTERS);
    return list.length ? list : undefined;
  });

/**
 * GET /clients query params — pagination plus the optional inbox filters
 * (AND-composed, same semantics as the inbox FilterOptions predicates).
 * Booleans arrive as 'true'/'false' strings; tags arrive repeated
 * (?tags=a&tags=b) or single. `view=your` resolves against the caller's JWT
 * email server-side. `afterTs`/`afterId` seed the first filtered page below
 * an already-loaded window (honored only when no cursor is passed).
 */
export const clientListQuerySchema = z.object({
  // cursor/limit predate this schema on a public endpoint — stay lenient so
  // existing integrators keep their behavior (empty cursor = first page,
  // junk/fractional limit = server default via clamp). The NEW filter params
  // below are strict: invalid values 400 instead of silently matching all.
  cursor: z
    .string()
    .optional()
    .transform(v => (v ? v : undefined)),
  limit: z.coerce.number().optional().catch(undefined),
  tags: tagListParam,
  isOpen: boolParam.optional(),
  unread: boolParam.optional(),
  assigned: boolParam.optional(),
  isBotReply: boolParam.optional(),
  view: z.enum(['all', 'your']).optional(),
  afterTs: z.coerce.number().int().positive().optional(),
  afterId: z.string().uuid().optional(),
});

export type ClientListQuery = z.infer<typeof clientListQuerySchema>;

/** Server-side filter set consumed by fetchClientListPaginated. */
export interface ClientListFilters {
  tags?: string[];
  isOpen?: boolean;
  unread?: boolean;
  assigned?: boolean;
  isBotReply?: boolean;
  /** Resolved from the caller's JWT for view=your — never from raw input. */
  assignedEmail?: string;
}

/** Frontend → GET /clients filter params (pre-serialization shape). */
export interface ClientFilterParams {
  view?: 'your';
  isOpen?: boolean;
  unread?: boolean;
  assigned?: boolean;
  isBotReply?: boolean;
  tags?: string[];
}
